import { Button } from '@/global/components/Button'
import Menu from '@/global/components/Menu'
import { IconButton, Box, SxProps, Theme } from '@mui/material'
import { FormFieldAutocomplete } from 'global/components/Form'
import Dialog from 'global/guides/Dialog'
import { HousePlusIcon, PlusIcon } from 'lucide-react'
import React from 'react'
import { SubmitHandler, useFormContext } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import {
    CreateCentroCustoOutput,
    CreateCentroCustoSchema,
    centroCusto,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { FormCentroCusto } from '../forms/FormCentroCusto'

type IsMenuItem = {
    item: boolean
    autoComplete?: false
    multiple?: false
    adornment?: false
}

type IsAutoComplete = {
    item?: false
    autoComplete: boolean
    multiple?: false
    adornment?: false
}

type IsButton = {
    item?: false
    autoComplete?: false
    multiple?: false
    adornment?: false
}

type isMultiple = {
    item?: false
    autoComplete?: false
    multiple: boolean
    adornment?: false
}

type isAdornment = {
    adornment?: boolean
    item?: false
    autoComplete?: false
    multiple?: false
}

type IProps = (
    | IsMenuItem
    | IsAutoComplete
    | IsButton
    | isMultiple
    | isAdornment
) & { className?: string }

/** @deprecated */
export const AdicionarCentroCusto: React.FC<IProps> = ({
    autoComplete,
    item,
    multiple,
    adornment,
    className,
}) => {
    const [open, setOpen] = React.useState(false)
    const empresaId = useStore(state => state.companyId)
    const navigate = useNavigate()

    const toggle = () => setOpen(state => !state)

    const context = useFormContext()

    if (autoComplete && !context) {
        throw new Error('Contexto de formulário não encontrado')
    }

    const isButton = !item && !autoComplete && !multiple && !adornment

    const { data } = centroCusto.useFetch({
        queryParams: { empresaId },
    })

    const { mutate, isLoading } = centroCusto.mutations.useCreate({
        invalidateQuery: true,
    })

    const handleSubmit: SubmitHandler<CreateCentroCustoOutput> = ({
        predecessorId,
        ...data
    }) => {
        const payload = predecessorId
            ? { ...data, empresaId, predecessorId }
            : { ...data, empresaId, predecessorId: null }

        mutate(payload, {
            onSuccess: data => {
                toast.success('Centro de Custo adicionado com sucesso!')

                if (autoComplete && context) {
                    context.setValue('FuncionarioCentroCusto.CentroCusto', data)
                }

                toggle()
            },
        })
    }

    return (
        <div className={className}>
            {adornment && (
                <IconButton
                    title="Novo centro de custo"
                    sx={{
                        mr: 3,
                        color: 'text.primary',
                    }}
                    onClick={() =>
                        navigate('/cadastros/centros-custo/adicionar')
                    }
                >
                    <HousePlusIcon />
                </IconButton>
            )}
            {item && (
                <Menu.Item onClick={toggle}>Novo centro de custo</Menu.Item>
            )}
            {isButton && (
                <Button
                    startIcon={<PlusIcon />}
                    variant="contained"
                    onClick={toggle}
                >
                    Adicionar
                </Button>
            )}
            {autoComplete && (
                <FormFieldAutocomplete
                    name="FuncionarioCentroCusto.CentroCusto"
                    label="Centro de Custo"
                    options={data || []}
                    getOptionLabel={option =>
                        option.nomeCentroCusto + ' - ' + option.codigo
                    }
                    creatableOption={{
                        label: 'Novo Centro de Custo',
                        onClick: toggle,
                    }}
                />
            )}
            {multiple && (
                <FormFieldAutocomplete
                    name="centroCustoIds"
                    label="Centros custo"
                    options={data || []}
                    getOptionLabel={option =>
                        option.nomeCentroCusto + ' - ' + option.codigo
                    }
                    placeholder={'Selecione uma ou mais centro custo'}
                    multiple
                    limitTags={2}
                    creatableOption={{
                        label: 'Novo Centro de Custo',
                        onClick: toggle,
                    }}
                />
            )}
            {open && (
                <Dialog.Root
                    open={open}
                    loading={isLoading}
                    height="12rem"
                    width="34rem"
                    schema={CreateCentroCustoSchema}
                    onSubmit={handleSubmit}
                    onClose={toggle}
                    defaultValues={{
                        predecessorId: 0,
                        empresaId,
                    }}
                >
                    <Dialog.Title>Adicionar Centro de Custo</Dialog.Title>
                    <Dialog.Content dividers>
                        <FormCentroCusto />
                    </Dialog.Content>
                    <Dialog.Actions>
                        <Dialog.Actions.SecondaryButton />
                        <Dialog.Actions.PrimaryButton />
                    </Dialog.Actions>
                </Dialog.Root>
            )}
        </div>
    )
}
